'use strict';

function applyCoreOptions(target, { RequestMethod }) {
  /** @prop {boolean} [enabled=true] If false, the plugin won't send NQS requests. */
  target.enabled = true;

  /**
   * @prop {string} [authToken]
   * Optional auth token to validate all the requests
   */
  target.authToken = undefined;

  /**
   * @prop {string} [authType]
   * Optional auth type. Used if authToken is set.
   * 'Bearer' by default.
   */
  target.authType = 'Bearer';

  /**
   *  @prop {boolean} [preventZombieViews=true]
   * If true, the plugin will check if the last event
   * was sent more than 10 mins ago
   * so it will not send more events to the same view
   */
  target.preventZombieViews = true;

  /**
   * @prop {boolean} [offline=false]
   * If true the plugin will store the events and send them later when there's connection
   */
  target.offline = false;

  /** @prop {string} [referer] Site url.
   * By default, window.location.href
   */
  target.referer = undefined;

  /** @prop {string} [referral] Previous site url.
   * By default, document.referrer
   */
  target.referral = undefined;

  /**
   * @prop {boolean} [disableCookies]
   * To disable cookies storage fallback after local/sessionstorage
   * True by default
   */
  target.disableCookies = true;

  /**
   * @prop {boolean} [forceCookies]
   * To force the use of cookies storage instead of local/sessionstorage
   * False by default
   */
  target.forceCookies = false;

  /**
   * @prop {boolean} [disableStorage]
   * To disable all possible storages usage (cookies, localStorage, sessionStorage)
   * CAUTION: enabling this option session tracking may stop to work properly
   * False by default
   */
  target.disableStorage = false;

  /**
   * @prop {number} [maxWaitingRequests=25]
   * Maximum number of requests that can be waiting for session token before dropping new requests
   * Default: 25
   */
  target.maxWaitingRequests = 25;

  /**
   * @prop {string} [userId]
   * User ID value inside your system.
   */
  target['userId'] = undefined;

  /**
   * @prop {string} [profileId]
   * Profile ID value inside your system.
   */
  target['profileId'] = undefined;

  /**
   * @prop {bool} [forceInit]
   * Forces init to be sent instead of start, use it when mediaduration,  title, source or is live is reported with a wrong value by the player until jointime happens
   */
  target.forceInit = false;

  /**
   * @prop {('default'|'legacy')} [joinTime.calculationMode]
   * Controls how the content `joinDuration` is computed when pre-roll ads are present.
   * Equivalent to `joinTimeCalculationMode` in the iOS/Android SDKs.
   * - 'default': measure from the last `adStop` until `/join` (parity with iOS/Android).
   * - 'legacy': (init/start -> join) minus the total duration of all ads in the break.
   * When unset, it falls back to the deprecated `joinTime.measureAfterAdBreak`
   * (true => 'default', false => 'legacy'), so the effective default is 'default'.
   */
  target['joinTime.calculationMode'] = undefined;

  /**
   * @prop {boolean} [joinTime.measureAfterAdBreak=true]
   * @deprecated Use `joinTime.calculationMode` instead ('default' | 'legacy'). Kept for backwards
   * compatibility: true maps to 'default', false maps to 'legacy'. If `joinTime.calculationMode`
   * is set, it takes precedence over this option.
   * If true, join time is measured from the end of an ad break to the start of content playback
   * when ads are present.
   */
  target['joinTime.measureAfterAdBreak'] = true;

  /** @prop {bool} [simpleNewSession] Enable the newSession behaviour to replace the fireStartSessio/begin methods. */
  target.simpleNewSession = false;

  /**
   * @prop {object} [session.metrics]
   * Item containing metrics in json format. Reported every beat if the values change
   */
  target['session.metrics'] = {};

  /**
   * @prop {bool} [session.context]
   * Boolean to choose to report context or not. False by default
   */
  target['session.context'] = false;

  /**
   * @prop {array<string>} [errors.fatal=[]]
   * If it has elements on it, all the errors matching this code will fire the stop event to end the view
   */
  target['errors.fatal'] = [];

  /**
   * @prop {array<string>} [errors.nonFatal=[]]
   * If it has elements on it, all the errors matching this code won't fire a stop event to end the view
   */
  target['errors.nonFatal'] = [];

  /**
   * @prop {array<string>} [errors.ignore=[]]
   * If it has elements on it, all the errors matching this code wont be reported
   */
  target['errors.ignore'] = [];

  /**
   * @prop {boolean} [pause.ignoreSmallEvents]
   * If flag is true, the plugin will ignore send pauseDuration value in pauses with less than 50 milliseconds
   */
  target['pause.ignoreSmallEvents'] = true;

  /**
   * @prop {boolean} [check.playerExists]
   * If flag is true, the plugin and adapter have the responsibility to check if player exists in the document, and if not exists, will send stop event
   */
  target['check.playerExists'] = true;

  /**
   * Auto Start AppAnalytics module
   * @prop {boolean} [check.appAnalytics.autoBegin]
   */
  target['check.appAnalytics.autoBegin'] = false;

  /**
   * Enable AppAnalytics module
   * @prop {boolean} [check.appAnalytics.autoBegin]
   */
  target['check.appAnalytics.enabled'] = true;

  /**
   * Send PlayerEvents Logs to QATools module
   * @prop {boolean} [debug.playerEvents.show]
   */
  target['debug.playerLogs.enabled'] = false;

  /**
   * Send Plugin Logs to QATools module
   * @prop {boolean} [debug.pluginLogs.show]
   */
  target['debug.pluginLogs.enabled'] = false;

  /**
   * Enable VideoAnalytics module
   * @type {boolean}
   */
  target['check.videoAnalytics.enabled'] = true;

  /**
   * Enable get configuration from LMA periodically
   * @type {boolean}
   */
  target['lma.refresh.configuration.enabled'] = false;

  /**
   * Time to get configuration from LMA service
   * @type {int}
   */
  target['lma.refresh.configuration.seconds'] = 300;

  /**
   * @prop {string} linkedViewId
   * String to send on start events to link views with previous session events
   */
  target.linkedViewId = undefined;

  /**
   * @prop {bool} [waitForMetadata]
   * Boolean to delay the start event. Use with `pendingMetadata`
   */
  target.waitForMetadata = false;

  /**
   * @prop {int} [waitForMetadata.timeout.milliseconds]
   * Configured timeout to wait for metadata parameters (default 10 seconds)
   */
  target['waitForMetadata.timeout.milliseconds'] = 10000;

  /**
   * @prop {array<string>} [pendingMetadata]
   * List of values that should be ready to send in start event. Use with `waitForMetadata` set to True.
   */
  target.pendingMetadata = [];

  /**
   * @prop {string} method
   * Method used to send request to backend side
   * Options are 'get' or 'post'
   * 'post' by default
   */
  target.method = RequestMethod.POST;

  /**
   * @prop {bool} [playhead.monitor]
   * Method to enabled/disable support for playhead monitor method
   */
  target['playhead.monitor'] = true;

  /**
   * @prop {bool} [network.monitor]
   * Method to enabled/disable support for readyState monitor method
   */
  target['readyState.monitor'] = true;

  /**
   * @prop {boolean} [adapters-json.overwrite]
   * If flag is false, only use json defined by customer, and don't get it from options source. (false by default).
   */
  target['adapters.overwrite'] = false;

  /**
   * @prop {object} adapters-json
   * Define adapters-json to update if it's possible (adapters-json templates to overwrite)
   */
  target['adapters.templates'] = {};

  /**
   * @prop {object}
   * Defined adapter custom properties (to condition adapter logic)
   */
  target['adapters.properties'] = {};

  /**
   * @prop {bool} [enableExtraMetricCollection]
   * If true it will enable the collection of extra metrics like accurate location, resource usage and network information
   * This might ask the user permission to access location
   */
  target.enableExtraMetricCollection = false;

  /**
   * @prop {number} [renditionQueryInterval]
   * Defines the time interval in milliseconds for the rendition query to the adapter for players that don't
   * support listeners for quality changes
   */
  target.renditionQueryInterval = 250;

  /**
   * @prop {bool} [enableLegacyBufferBehaviour]
   * Defines if legacy buffer behavior should be used (ignore audio buffers)
   */
  target['enableLegacyBufferBehaviour'] = false;

  /**
   * @prop {bool} [ignoreSeekInTimeUpdate]
   * Defines if seeking in timeupdate listerner should be ignored if seeking to the same position (false by default).
   */
  target.ignoreSeekInTimeUpdate = false;

  /**
   * @prop {bool} [checkVideoBufferInPlayheadMonitor]
   * Defines if video buffer should be checked in playhead monitor before triggering a buffer begin (false by default).
   */
  target.checkVideoBufferInPlayheadMonitor = false;
}

module.exports = applyCoreOptions;

